import classNames from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import React, { useEffect, useRef, useState } from 'react';

import { setLanguage } from '@/api/customer';
import CONSTANTS from '@/constants/const';
import callAppFunction from '@/utils/bridge';
import { isSayWeeeApp, setCookie } from '@/utils/tool';
import { getLoginState } from '@/utils/business';
import useClickActionT2 from '@/tracks/useClickActionT2';

import styles from './index.module.scss';
import { useSupportLanguages } from '@/hooks/useSupportLanguages';
import { CaratDown, CheckmarkOutline, GlobeOutline } from '@weee-fe/kunlunicon';

type Props = {
  className?: string;
  showLabel?: boolean;
  defaultVisible?: boolean;
  supportLanguages?: any[];
  clickTrack?: () => void;
  chooseLanguageTrack?: (lang) => void;
  onBeforeSendTrack?: (params: ObjectType) => ObjectType;
};
export default function ChangeLangue(props: Props) {
  const { className, showLabel = true, defaultVisible = false, chooseLanguageTrack, onBeforeSendTrack } = props;
  const { t, lang } = useTranslation('common');
  const [visible, setVisible] = useState(false);
  const { languages, loading } = useSupportLanguages();
  const { buttonClickView } = useClickActionT2();

  const onScrollPage = () => {
    setVisible(false);
  };
  useEffect(() => {
    window.addEventListener('scroll', onScrollPage);
    return () => {
      window.removeEventListener('scroll', onScrollPage);
    };
  }, []);
  useEffect(() => {
    if (defaultVisible) {
      let timer = setTimeout(() => {
        setVisible(true);
      }, 1000);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [defaultVisible]);

  const switchLanguage = (newLang: string) => {
    if (newLang !== lang) {
      setCookie(
        CONSTANTS.LANGUAGE,
        newLang === CONSTANTS.LANGUAGE_ZHT ? CONSTANTS.LANGUAGE_ZHT_ALIAS : newLang,
        {},
        {
          maxAge: CONSTANTS.LANGUAGE_EXPIRED_TIME
        }
      );
      if (isSayWeeeApp()) {
        callAppFunction('changeLanguage', { language: newLang }, null);
        return;
      }
      window.location.reload();
    }
  };

  const handleTracking = lng => {
    if (chooseLanguageTrack) {
      chooseLanguageTrack(lng);
    } else {
      let params = {
        co: {
          target_nm: lng
        }
      };
      buttonClickView(onBeforeSendTrack ? onBeforeSendTrack(params) : params);
    }
  };

  const changeLanguage = e => {
    props?.clickTrack && props?.clickTrack();
    let lng = e.target.dataset.language;
    if (lng !== lang) {
      lng = lng === CONSTANTS.LANGUAGE_CHINESE_T ? CONSTANTS.LANGUAGE_CHINESE_T_ALIAS : lng;
      let isLogin = getLoginState();
      handleTracking(lng);
      if (isLogin) {
        return setLanguage(lng).then(({ result }) => {
          if (result) {
            switchLanguage(lng);
          }
        });
      }
      return switchLanguage(lng);
    }
  };

  return (
    <CustomPopover
      visible={visible}
      content={
        <>
          {!loading && (
            <>
              {languages.map(item => {
                return (
                  <div key={item.key} onClick={changeLanguage} data-language={item.key} className={styles.languageItem}>
                    {item.label}
                    {lang === item.key && <CheckmarkOutline className="text-primary-2 text-base" />}
                  </div>
                );
              })}
            </>
          )}
        </>
      }>
      <div
        className={classNames(styles.language, className, 'language-popover')}
        onClick={() => {
          setVisible(true);
        }}>
        <GlobeOutline className="text-navbar-fg-default text-lg" />
        {showLabel && <>{t('onboarding_language')}</>}
        <CaratDown className="text-surface-100-fg-default text-xl" />
      </div>
    </CustomPopover>
  );
}

type PopoverProps = {
  content: any;
  children: React.ReactElement;
  visible?: boolean;
};

const CustomPopover: React.FC<PopoverProps> = ({ content, children, visible }) => {
  const [showPopover, setShowPopover] = useState(visible);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [clickPosition, setClickPosition] = useState({ x: 0, y: 0 });

  const handleClickOutside = (event: MouseEvent) => {
    if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
      setShowPopover(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('scroll', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('scroll', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setShowPopover(visible);
    const rect = popoverRef.current?.getBoundingClientRect();
    if (rect) {
      setClickPosition({ x: rect.x + rect.width / 2, y: rect.y + rect.height });
    }
    return () => {
      setShowPopover(visible);
    };
  }, [visible]);

  return (
    <div ref={popoverRef}>
      <div
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          // 获取popoverRef的中间位置
          const { x, y, width, height } = popoverRef.current.getBoundingClientRect();
          setClickPosition({ x: x + width / 2, y: y + height });
          setShowPopover(!showPopover);
        }}>
        {children}
      </div>
      {showPopover && (
        <div
          className="fixed bg-surface-100-bg rounded-200 -translate-x-2/3 shadow-lg overflow-hidden z-[1]"
          style={{
            top: clickPosition.y,
            left: clickPosition.x
          }}>
          {content}
        </div>
      )}
    </div>
  );
};
